import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
  Platform,
  FlatList,
  Dimensions,
  Animated,
} from 'react-native';
import { WebView } from 'react-native-webview';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, textStyles } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { getImageUrlSync, getApiBaseURL } from '../../utils/imageProxy';
import { getCurrencySymbol } from '../../utils/currencies';
import { useCartStore, SelectedAddon } from '../../store/cartStore';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useOrderMinimums } from '../../hooks/useOrderMinimums';

interface ProductViewContentProps {
  product: any;
  apiBaseURL?: string | null;
  onAddToCart?: (productId: string, variantId?: string) => void;
  /** Cart badge tap — previously hardcoded to a web-only window.location.href
   * hard reload (no native equivalent existed at all); this component has no
   * navigation context of its own, so the caller supplies it. */
  onGoToCart?: () => void;
  containerStyle?: any;
  scrollViewStyle?: any;
  contentContainerStyle?: any;
  showAddToCart?: boolean;
}

const hexAlpha = (hex: string, alpha: number) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// Detects whether a string contains HTML tags
const isHtml = (str: string) => /<[a-z][\s\S]*>/i.test(str);

// Renders HTML description: dangerouslySetInnerHTML on web, WebView on native
const ProductDescription: React.FC<{ html: string }> = ({ html }) => {
  const [height, setHeight] = useState(200);
  const { isRTL } = useLocale();
  const textColor = String(colors.text.primary);
  const headingColor = String(colors.text.primary);
  const linkColor = String(colors.primary);
  const content = isHtml(html) ? html : `<p>${html.replace(/\n/g, '<br/>')}</p>`;
  const styledHtml = `
    <!DOCTYPE html><html><head>
    <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
             font-size: 15px; line-height: 1.6; color: ${textColor}; padding: 0; background: transparent;
             direction: ${isRTL ? 'rtl' : 'ltr'}; text-align: ${isRTL ? 'right' : 'left'}; }
      p { margin-bottom: 10px; }
      ul, ol { padding-left: 20px; margin-bottom: 10px; }
      li { margin-bottom: 4px; }
      strong, b { font-weight: 700; }
      em, i { font-style: italic; }
      h1,h2,h3,h4 { font-weight: 700; margin-bottom: 8px; color: ${headingColor}; }
      img { max-width: 100%; height: auto; border-radius: 8px; }
      a { color: ${linkColor}; }
    </style></head>
    <body>${content}
    <script>
      window.onload = function() {
        window.ReactNativeWebView && window.ReactNativeWebView.postMessage(
          JSON.stringify({ height: document.body.scrollHeight })
        );
      };
    </script>
    </body></html>`;

  if (Platform.OS === 'web') {
    return (
      <div
        style={{ fontSize: 15, lineHeight: 1.6, color: textColor }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  return (
    <WebView
      originWhitelist={['*']}
      source={{ html: styledHtml }}
      style={{ width: '100%', height, backgroundColor: 'transparent' }}
      scrollEnabled={false}
      onMessage={(e) => {
        try {
          const { height: h } = JSON.parse(e.nativeEvent.data);
          if (h && h > 0) setHeight(h + 16);
        } catch {}
      }}
      showsVerticalScrollIndicator={false}
    />
  );
};

export const ProductViewContent: React.FC<ProductViewContentProps> = ({
  product,
  apiBaseURL: propApiBaseURL,
  onAddToCart,
  onGoToCart,
  containerStyle,
  scrollViewStyle,
  contentContainerStyle,
  showAddToCart = true,
}) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  // Width of this view, not the window — it's also rendered inside a
  // centered column / bottom sheet narrower than the screen on desktop web.
  const [layoutWidth, setLayoutWidth] = useState(() => Dimensions.get('window').width);
  const screenWidth = layoutWidth;
  const [addonError, setAddonError] = useState<string | null>(null);
  const minQty = Math.max(1, Number(product?.minimumOrderQuantity) || 1);
  // Vendor minimum order amounts for this product's vendors (shown as a note).
  const productIdList = useMemo(() => (product?._id ? [String(product._id)] : []), [product?._id]);
  const noLines = useMemo(() => [], []);
  const { report: vendorMinimums } = useOrderMinimums(product?.businessId, noLines, undefined, productIdList);
  const imageHeight = Math.min(400, screenWidth);
  
  const { addItem, getItemQuantity, updateQuantity, removeItem, items, totalItems } = useCartStore();
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(propApiBaseURL || null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [failedImageUrls, setFailedImageUrls] = useState<Set<string>>(new Set());
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  // Addons: map of addonId -> quantity selected (0 = not selected)
  const [addonSelections, setAddonSelections] = useState<Record<string, number>>({});
  const loadedImageUrlsRef = useRef<Set<string>>(new Set());
  const imageGalleryRef = useRef<FlatList>(null);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const isScrollingProgrammaticallyRef = useRef(false);

  // Load API base URL if not provided
  useEffect(() => {
    if (!propApiBaseURL) {
      const loadApiConfig = async () => {
        try {
          const baseURL = await getApiBaseURL();
          setApiBaseURL(baseURL);
        } catch (error) {
          console.error('[ProductViewContent] Failed to load API config:', error);
        }
      };
      loadApiConfig();
    }
  }, [propApiBaseURL]);

  // Auto-select first variant on product load.
  // Also remove any no-variant cart entry for this product — it was added incorrectly
  // (e.g. from a grid "+" tap that didn't go through variant selection).
  useEffect(() => {
    if (product?.hasVariants && product?.variants && product.variants.length > 0) {
      if (!selectedVariantId) {
        setSelectedVariantId(product.variants[0].id);
      }
      // Remove the bare product entry (no variantId) if it exists
      const bareEntry = items.find(i => i.productId === product._id && !i.variantId);
      if (bareEntry) removeItem(product._id, undefined);
    }
  }, [product._id]);

  // Reset image index when product or variant changes
  useEffect(() => {
    setSelectedImageIndex(0);
    // Scroll to first image when product/variant changes
    // Use a longer delay to ensure FlatList is fully rendered
    const timeoutId = setTimeout(() => {
      if (imageGalleryRef.current) {
        try {
          imageGalleryRef.current.scrollToIndex({ index: 0, animated: false });
        } catch (error) {
          // Fallback to scrollToOffset
          imageGalleryRef.current.scrollToOffset({ offset: 0, animated: false });
        }
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [product?._id, selectedVariantId]);


  const isValidBlobUrl = useCallback((url: string): boolean => {
    if (!url.startsWith('blob:')) return false;
    try {
      const blobUrlPattern = /^blob:https?:\/\/[^/]+\/[a-f0-9-]+$/i;
      return blobUrlPattern.test(url);
    } catch {
      return false;
    }
  }, []);

  const getImageUrl = useCallback((url: string): string => {
    return getImageUrlSync(url, apiBaseURL);
  }, [apiBaseURL]);

  const handleImageError = useCallback((url: string, isBlob: boolean, originalUrl?: string) => {
    if (isBlob) {
      console.warn('[ProductViewContent] Blob URL expired, revoked, or invalid:', url);
      setFailedImageUrls(prev => {
        const next = new Set(prev);
        next.add(url);
        if (originalUrl && url !== originalUrl) {
          next.add(originalUrl);
        }
        return next;
      });
    } else if (Platform.OS === 'web' && url.includes('firebasestorage.googleapis.com') && !apiBaseURL) {
      console.log('[ProductViewContent] Firebase image failed but apiBaseURL not ready, will retry:', url);
    } else {
      console.error('[ProductViewContent] Failed to load image:', url);
      setFailedImageUrls(prev => {
        const next = new Set(prev);
        next.add(url);
        if (originalUrl && url !== originalUrl) {
          next.add(originalUrl);
        }
        return next;
      });
    }
  }, [apiBaseURL]);

  const handleImageLoadSuccess = useCallback((imageUrl: string, originalUrl: string) => {
    const key = `${imageUrl}|${originalUrl}`;
    if (loadedImageUrlsRef.current.has(key)) {
      return;
    }
    
    loadedImageUrlsRef.current.add(key);
    loadedImageUrlsRef.current.add(imageUrl);
    loadedImageUrlsRef.current.add(originalUrl);
    
    setFailedImageUrls(prev => {
      if (prev.has(imageUrl) || prev.has(originalUrl)) {
        const next = new Set(prev);
        next.delete(imageUrl);
        next.delete(originalUrl);
        return next;
      }
      return prev;
    });
  }, []);

  const renderImageItem = useCallback(({ item, index }: { item: string; index: number }) => {
    const imageUrl = getImageUrl(item);
    const isBlobUrl = item.startsWith('blob:');
    const isFirebaseUrl = item.includes('firebasestorage.googleapis.com');
    const needsApiBase = Platform.OS === 'web' && isFirebaseUrl && !apiBaseURL;
    const hasFailed = failedImageUrls.has(imageUrl) || failedImageUrls.has(item);
    const isEmptyUrl = !imageUrl || imageUrl.trim() === '';
    
    if (Platform.OS === 'web') {
      if (isEmptyUrl) {
        return (
          <View style={{ width: screenWidth, height: imageHeight, backgroundColor: colors.lightGray, justifyContent: 'center', alignItems: 'center' }}>
            <Ionicons name="image-outline" size={64} color={colors.textLight} />
            <Text style={{ marginTop: spacing.sm, color: colors.textLight, fontSize: 14 }}>{t('storefront.imageUnavailable')}</Text>
          </View>
        );
      }
      
      if (needsApiBase) {
        return (
          <View style={{ width: screenWidth, height: imageHeight, backgroundColor: colors.lightGray, justifyContent: 'center', alignItems: 'center' }}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        );
      }
      
      if (hasFailed) {
        return (
          <View style={{ width: screenWidth, height: imageHeight, backgroundColor: colors.lightGray, justifyContent: 'center', alignItems: 'center' }}>
            <Ionicons name="image-outline" size={64} color={colors.textLight} />
            <Text style={{ marginTop: spacing.sm, color: colors.textLight, fontSize: 14 }}>
              {t('storefront.imageUnavailable')}
            </Text>
          </View>
        );
      }
      
      return (
        <View style={{ width: screenWidth, height: imageHeight, backgroundColor: colors.lightGray }}>
          <Image
            source={{ uri: imageUrl }}
            style={{ width: screenWidth, height: imageHeight } as any}
            resizeMode="cover"
            onError={(error) => {
              console.error('[ProductViewContent] Image load error:', error.nativeEvent?.error || error);
              handleImageError(imageUrl || item, isBlobUrl, item);
            }}
            onLoad={() => handleImageLoadSuccess(imageUrl, item)}
          />
        </View>
      );
    }
    
    if (isEmptyUrl) {
      return (
        <View style={{ width: screenWidth, height: imageHeight, backgroundColor: colors.lightGray, justifyContent: 'center', alignItems: 'center' }}>
          <Ionicons name="image-outline" size={64} color={colors.textLight} />
          <Text style={{ marginTop: spacing.sm, color: colors.textLight, fontSize: 14 }}>{t('storefront.imageUnavailable')}</Text>
        </View>
      );
    }
    
    if (hasFailed) {
      return (
        <View style={{ width: screenWidth, height: imageHeight, backgroundColor: colors.lightGray, justifyContent: 'center', alignItems: 'center' }}>
          <Ionicons name="image-outline" size={64} color={colors.textLight} />
          <Text style={{ marginTop: spacing.sm, color: colors.textLight, fontSize: 14 }}>
            {t('storefront.imageUnavailable')}
          </Text>
        </View>
      );
    }
    
    return (
      <TouchableOpacity
        activeOpacity={0.9}
        style={{ width: screenWidth, height: imageHeight }}
      >
        <Image 
          source={{ uri: imageUrl }} 
          style={{ width: screenWidth, height: imageHeight } as any}
          resizeMode="cover"
          onError={(error) => {
            console.error('[ProductViewContent] Image load error:', error.nativeEvent?.error || error);
            handleImageError(imageUrl || item, isBlobUrl, item);
          }}
          onLoad={() => handleImageLoadSuccess(imageUrl, item)}
        />
      </TouchableOpacity>
    );
  }, [getImageUrl, apiBaseURL, failedImageUrls, screenWidth, imageHeight, handleImageError, handleImageLoadSuccess]);

  if (!product) {
    return null;
  }

  // Get selected variant
  const selectedVariant = product.hasVariants && product.variants && selectedVariantId
    ? product.variants.find((v: any) => v.id === selectedVariantId)
    : null;

  // Get images - use variant images if available, otherwise product images
  const productImages = selectedVariant && selectedVariant.images && selectedVariant.images.length > 0
    ? selectedVariant.images.filter(Boolean)
    : product.images && product.images.length > 0 
      ? product.images.map((img: any) => img.url).filter(Boolean)
      : product.primaryImage 
        ? [product.primaryImage]
        : [];

  // Get price - use variant price if available, otherwise product price
  const basePrice = selectedVariant && selectedVariant.price !== undefined
    ? selectedVariant.price
    : product.price;

  const displayPrice = product.salePrice && product.salePrice < basePrice && !selectedVariant
    ? product.salePrice
    : basePrice;

  const originalPrice = product.salePrice && product.salePrice < basePrice && !selectedVariant
    ? basePrice
    : null;

  const discountPercent = originalPrice
    ? Math.round(((originalPrice - displayPrice) / originalPrice) * 100)
    : 0;

  // Get availability - use variant availability if available, otherwise product availability
  const variantAvailability = selectedVariant && selectedVariant.inventoryQuantity !== undefined
    ? selectedVariant.inventoryQuantity > 0 ? 'in_stock' : 'out_of_stock'
    : null;

  const currentAvailability = variantAvailability || product.availability;

  const availabilityColor = currentAvailability === 'in_stock' 
    ? colors.success 
    : currentAvailability === 'out_of_stock'
    ? colors.error
    : colors.warning;

  const availabilityText = currentAvailability === 'in_stock'
    ? t('storefront.inStock')
    : currentAvailability === 'out_of_stock'
    ? t('storefront.outOfStock')
    : currentAvailability === 'preorder'
    ? t('storefront.preorder')
    : t('storefront.backorder');

  // Handle variant selection
  const handleVariantSelect = (variantId: string) => {
    setSelectedVariantId(variantId);
    setSelectedImageIndex(0);
    imageGalleryRef.current?.scrollToIndex({ index: 0, animated: true });
  };
  
  // Build selected addons array from addonSelections state
  const buildSelectedAddons = useCallback((): SelectedAddon[] => {
    if (!product?.addons) return [];
    return product.addons
      .filter((a: any) => (addonSelections[a.id] || 0) > 0)
      .map((a: any) => ({
        addonId: a.id,
        name: a.name,
        price: a.price,
        quantity: addonSelections[a.id] || 1,
      }));
  }, [product, addonSelections]);

  // Stock limit for the currently selected variant or base product
  const stockLimit = useMemo(() => {
    if (selectedVariantId && product?.variants) {
      const v = product.variants.find((v: any) => v.id === selectedVariantId);
      if (v?.inventoryQuantity !== undefined) return v.inventoryQuantity;
    }
    if (product?.inventoryTracking && product?.inventoryQuantity !== undefined) {
      return product.inventoryQuantity;
    }
    return Infinity;
  }, [product, selectedVariantId]);

  // Handle add to cart
  const handleAddToCart = useCallback(() => {
    if (!product._id) return;
    const missingRequired = (product.addons || []).filter((a: any) => a.isRequired && !(addonSelections[a.id] > 0));
    if (missingRequired.length > 0) {
      setAddonError(t('storefront.requiredAddonsMissing', { names: missingRequired.map((a: any) => a.name).join(', ') }));
      return;
    }
    setAddonError(null);
    const variantId = selectedVariantId || undefined;
    const selectedAddons = buildSelectedAddons();
    addItem({
      productId: product._id,
      variantId,
      product: {
        _id: product._id,
        name: product.name,
        price: product.price,
        salePrice: product.salePrice,
        currency: product.currency,
        primaryImage: product.primaryImage,
        images: product.images,
        brand: product.brand,
        sku: product.sku,
        availability: product.availability,
        hasVariants: product.hasVariants,
        inventoryTracking: product.inventoryTracking,
        inventoryQuantity: product.inventoryQuantity,
        minimumOrderQuantity: product.minimumOrderQuantity,
        addons: product.addons,
      },
      variant: variantId && product.variants
        ? product.variants.find((v: any) => v.id === variantId)
        : undefined,
      selectedAddons: selectedAddons.length > 0 ? selectedAddons : undefined,
    }, (() => {
      // First add tops the product (all variants together) up to its minimum.
      const otherLines = items.filter((i) => i.productId === product._id && (i.variantId || undefined) !== variantId)
        .reduce((sum, i) => sum + i.quantity, 0);
      return Math.max(1, minQty - otherLines);
    })());
    if (onAddToCart) onAddToCart(product._id, variantId);
  }, [product, selectedVariantId, addItem, onAddToCart, buildSelectedAddons, addonSelections, t, items, minQty]);
  
  // Handle quantity changes
  const handleIncreaseQuantity = useCallback(() => {
    if (!product._id) return;
    const variantId = selectedVariantId || undefined;
    const currentQuantity = getItemQuantity(product._id, variantId);
    if (currentQuantity >= stockLimit) return;
    if (currentQuantity === 0) {
      handleAddToCart();
    } else {
      updateQuantity(product._id, variantId, currentQuantity + 1);
    }
  }, [product._id, selectedVariantId, getItemQuantity, updateQuantity, stockLimit, handleAddToCart]);
  
  const handleDecreaseQuantity = useCallback(() => {
    if (!product._id) return;
    const variantId = selectedVariantId || undefined;
    const currentQuantity = getItemQuantity(product._id, variantId);
    // Below the minimum (all variants together) the product can't be ordered — remove the line.
    const productTotal = items.filter((i) => i.productId === product._id).reduce((sum, i) => sum + i.quantity, 0);
    if (currentQuantity > 1 && productTotal - 1 >= minQty) {
      updateQuantity(product._id, variantId, currentQuantity - 1);
    } else {
      removeItem(product._id, variantId);
    }
  }, [product._id, selectedVariantId, getItemQuantity, updateQuantity, removeItem, items, minQty]);
  
  // Get current quantity for selected variant - make it reactive by using items from store
  const currentQuantity = useMemo(() => {
    if (!product._id) return 0;
    const variantId = selectedVariantId || undefined;
    const key = variantId ? `${product._id}_${variantId}` : product._id;
    const cartItem = items.find((item) => {
      const itemKey = item.variantId ? `${item.productId}_${item.variantId}` : item.productId;
      return itemKey === key;
    });
    return cartItem?.quantity || 0;
  }, [product._id, selectedVariantId, items]);

  const handleImageScroll = useCallback((event: any) => {
    // Don't update if we're programmatically scrolling
    if (isScrollingProgrammaticallyRef.current) {
      return;
    }
    // Compute productImages here to avoid initialization issues
    const images = selectedVariant && selectedVariant.images && selectedVariant.images.length > 0
      ? selectedVariant.images.filter(Boolean)
      : product.images && product.images.length > 0 
        ? product.images.map((img: any) => img.url).filter(Boolean)
        : product.primaryImage 
          ? [product.primaryImage]
          : [];
    
    const scrollPosition = event.nativeEvent?.contentOffset?.x || 0;
    const index = Math.floor(scrollPosition / screenWidth + 0.5); // Use floor with rounding for more accurate index
    if (index >= 0 && index < images.length && index !== selectedImageIndex) {
      setSelectedImageIndex(index);
    }
  }, [product, selectedVariant, screenWidth, selectedImageIndex]);

  const scrollToImage = useCallback((index: number) => {
    // Compute productImages here to avoid dependency issues
    const images = selectedVariant && selectedVariant.images && selectedVariant.images.length > 0
      ? selectedVariant.images.filter(Boolean)
      : product.images && product.images.length > 0 
        ? product.images.map((img: any) => img.url).filter(Boolean)
        : product.primaryImage 
          ? [product.primaryImage]
          : [];
    
    if (index < 0 || index >= images.length) return;
    if (index === selectedImageIndex) return; // Already at this index
    
    setSelectedImageIndex(index);
    isScrollingProgrammaticallyRef.current = true;
    
    // Use requestAnimationFrame to ensure FlatList is ready
    requestAnimationFrame(() => {
      if (imageGalleryRef.current) {
        // Try scrollToIndex first (works better with getItemLayout)
        try {
          imageGalleryRef.current.scrollToIndex({ 
            index, 
            animated: true,
            viewPosition: 0.5, // Center the item
          });
        } catch (error) {
          // Fallback to scrollToOffset if scrollToIndex fails
          const offset = index * screenWidth;
          imageGalleryRef.current.scrollToOffset({ 
            offset, 
            animated: true 
          });
        }
      }
      
      // Reset flag after animation completes (approximately 300ms)
      setTimeout(() => {
        isScrollingProgrammaticallyRef.current = false;
      }, 350);
    });
  }, [product, selectedVariant, screenWidth, selectedImageIndex]);

  const isOutOfStockProduct = currentAvailability === 'out_of_stock';

  return (
    <Animated.View
      style={[
        containerStyle || styles.container,
        { opacity: fadeAnim },
      ]}
      onLayout={(e) => {
        const w = Math.round(e.nativeEvent.layout.width);
        if (w > 0 && Math.abs(w - layoutWidth) > 1) setLayoutWidth(w);
      }}
    >
      <ScrollView
        style={scrollViewStyle || styles.content}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[contentContainerStyle || styles.contentContainer, showAddToCart && { paddingBottom: 90 }]}
        nestedScrollEnabled={true}
        scrollEnabled={true}
      >
        {/* Enhanced Image Gallery */}
        {productImages.length > 0 ? (
          <View style={styles.imageSection}>
            <View style={styles.imageGalleryContainer}>
              <View 
                style={{ 
                  height: imageHeight, 
                  width: screenWidth,
                  ...Platform.select({
                    web: {
                      overflow: 'hidden',
                      touchAction: 'pan-x',
                    },
                  }),
                }}
              >
                <FlatList
                  ref={imageGalleryRef}
                  horizontal
                  pagingEnabled
                  showsHorizontalScrollIndicator={false}
                  data={productImages}
                  keyExtractor={(item, index) => `image-${index}-${item}`}
                  renderItem={renderImageItem}
                  onScroll={handleImageScroll}
                  onMomentumScrollEnd={handleImageScroll}
                  scrollEventThrottle={16}
                  getItemLayout={(data, index) => ({
                    length: screenWidth,
                    offset: screenWidth * index,
                    index,
                  })}
                  contentContainerStyle={{
                    width: screenWidth * productImages.length,
                    height: imageHeight,
                  }}
                  onScrollToIndexFailed={(info) => {
                    setTimeout(() => {
                      imageGalleryRef.current?.scrollToOffset({ 
                        offset: info.index * screenWidth, 
                        animated: false 
                      });
                    }, 100);
                  }}
                  style={[styles.imageGallery, { width: screenWidth, height: imageHeight }]}
                  removeClippedSubviews={false}
                  key={`gallery-${product?._id || 'loading'}-${selectedVariantId || 'no-variant'}`}
                  initialScrollIndex={0}
                  nestedScrollEnabled={true}
                  scrollEnabled={true}
                  directionalLockEnabled={true}
                />
              </View>
              
              {/* Cart Badge - Top Right */}
              {totalItems > 0 && (
                <TouchableOpacity
                  style={styles.cartBadge}
                  onPress={onGoToCart}
                  activeOpacity={0.8}
                >
                  <Ionicons name="cart" size={16} color={colors.white} />
                  <Text style={styles.cartBadgeText}>
                    {totalItems > 99 ? '99+' : totalItems}
                  </Text>
                </TouchableOpacity>
              )}

              {/* Image Counter */}
              {productImages.length > 1 && (
                <View style={[
                  styles.imageCounter,
                  {
                    right: totalItems > 0 
                      ? spacing.md + 60 // Adjust if cart badge is present
                      : spacing.md
                  }
                ]}>
                  <Ionicons name="images" size={16} color={colors.white} />
                  <Text style={styles.imageCounterText}>
                    {selectedImageIndex + 1} / {productImages.length}
                  </Text>
                </View>
              )}

              {/* Product Name Footer Overlay */}
              <View style={styles.imageFooter}>
                <View style={styles.imageFooterContent}>
                  {product.brand && (
                    <Text style={styles.imageFooterBrand}>{product.brand}</Text>
                  )}
                  <Text style={styles.imageFooterName}>{product.name}</Text>
                </View>
              </View>
            </View>

            {/* Image Thumbnails - Only show if more than 1 image */}
            {productImages.length > 1 && (
              <View style={styles.thumbnailsContainer}>
                <View style={styles.thumbnailsRow}>
                  <ScrollView 
                    horizontal 
                    showsHorizontalScrollIndicator={false}
                    style={styles.thumbnailsScrollView}
                    contentContainerStyle={styles.thumbnailsScrollContent}
                  >
                    {productImages.map((img: string, index: number) => (
                      <TouchableOpacity
                        key={index}
                        onPress={() => scrollToImage(index)}
                        style={[
                          styles.thumbnail,
                          selectedImageIndex === index && styles.thumbnailActive,
                        ]}
                      >
                        {Platform.OS === 'web' ? (
                          (() => {
                            const thumbnailUrl = getImageUrl(img);
                            const isBlob = img.startsWith('blob:');
                            const isFirebase = img.includes('firebasestorage.googleapis.com');
                            const needsApiBase = Platform.OS === 'web' && isFirebase && !apiBaseURL;
                            const hasFailed = failedImageUrls.has(thumbnailUrl) || failedImageUrls.has(img);
                            
                            if (needsApiBase) {
                              return (
                                <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', backgroundColor: colors.lightGray }}>
                                  <ActivityIndicator size="small" color={colors.primary} />
                                </View>
                              );
                            }
                            
                            if (hasFailed) {
                              return (
                                <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', backgroundColor: colors.lightGray }}>
                                  <Ionicons name="image-outline" size={24} color={colors.textLight} />
                                </View>
                              );
                            }
                            
                            return (
                              <Image
                                source={{ uri: thumbnailUrl }}
                                style={styles.thumbnailImage as any}
                                resizeMode="cover"
                                onError={() => handleImageError(thumbnailUrl, isBlob, img)}
                                onLoad={() => handleImageLoadSuccess(thumbnailUrl, img)}
                              />
                            );
                          })()
                        ) : (
                          (() => {
                            const thumbnailUrl = getImageUrl(img);
                            const isBlob = img.startsWith('blob:');
                            const hasFailed = failedImageUrls.has(thumbnailUrl) || failedImageUrls.has(img);
                            
                            if (hasFailed) {
                              return (
                                <View style={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center', backgroundColor: colors.lightGray }}>
                                  <Ionicons name="image-outline" size={24} color={colors.textLight} />
                                </View>
                              );
                            }
                            
                            return (
                              <Image 
                                source={{ uri: thumbnailUrl }} 
                                style={styles.thumbnailImage as any}
                                onError={() => handleImageError(thumbnailUrl, isBlob, img)}
                                onLoad={() => handleImageLoadSuccess(thumbnailUrl, img)}
                              />
                            );
                          })()
                        )}
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                  {/* Add to Cart Button or Quantity Controls at the end of the line - Only when multiple images */}
                  {showAddToCart && product.availability !== 'out_of_stock' && (
                    currentQuantity > 0 ? (
                      <View style={styles.quantityControlsThumbnail}>
                        <TouchableOpacity
                          style={styles.quantityButton}
                          onPress={handleDecreaseQuantity}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                          <Ionicons name="remove-circle" size={24} color={colors.primary} />
                        </TouchableOpacity>
                        <Text style={styles.quantityTextThumbnail}>{currentQuantity}</Text>
                        <TouchableOpacity
                          style={styles.quantityButton}
                          onPress={handleIncreaseQuantity}
                          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                        >
                          <Ionicons name="add-circle" size={24} color={colors.primary} />
                        </TouchableOpacity>
                      </View>
                    ) : (
                      <TouchableOpacity
                        style={styles.addToCartButtonThumbnail}
                        onPress={handleAddToCart}
                        activeOpacity={0.8}
                      >
                        <Ionicons name="cart" size={24} color={colors.text.onPrimary} />
                      </TouchableOpacity>
                    )
                  )}
                </View>
              </View>
            )}
          </View>
        ) : (
          <View style={[styles.imagePlaceholder, { height: imageHeight }]}>
            <Ionicons name="cube-outline" size={64} color={colors.textLight} />
            <Text style={styles.placeholderText}>{t('storefront.noImage')}</Text>
          </View>
        )}

        {/* Product Info Card */}
        <View style={styles.productCard}>
          {/* Price Section - Enhanced */}
          <View style={styles.priceSection}>
            <View style={styles.priceRow}>
              <View style={styles.priceContainer}>
                <Text style={styles.price}>
                  {getCurrencySymbol(product.currency || 'USD')} {displayPrice.toFixed(2)}
                </Text>
                {originalPrice && !discountPercent && (
                  <Text style={styles.originalPrice}>
                    {getCurrencySymbol(product.currency || 'USD')} {originalPrice.toFixed(2)}
                  </Text>
                )}
              </View>
              <View style={styles.priceActions}>
                {discountPercent > 0 && (
                  <View style={styles.discountTagContainer}>
                    <View style={styles.discountTag}>
                      <Text style={styles.discountTagText}>-{discountPercent}%</Text>
                    </View>
                    {originalPrice && (
                      <Text style={styles.originalPriceBelowBadge}>
                        {getCurrencySymbol(product.currency || 'USD')} {originalPrice.toFixed(2)}
                      </Text>
                    )}
                  </View>
                )}
                {/* Add to Cart Button or Quantity Controls - Show here when there's only 1 image or no images */}
                {showAddToCart && product.availability !== 'out_of_stock' && productImages.length <= 1 && (
                  currentQuantity > 0 ? (
                    <View style={styles.quantityControlsInline}>
                      <TouchableOpacity
                        style={styles.quantityButton}
                        onPress={handleDecreaseQuantity}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      >
                        <Ionicons name="remove-circle" size={20} color={colors.primary} />
                      </TouchableOpacity>
                      <Text style={styles.quantityTextInline}>{currentQuantity}</Text>
                      <TouchableOpacity
                        style={styles.quantityButton}
                        onPress={handleIncreaseQuantity}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      >
                        <Ionicons name="add-circle" size={20} color={colors.primary} />
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <TouchableOpacity
                      style={styles.addToCartButtonInline}
                      onPress={handleAddToCart}
                      activeOpacity={0.8}
                    >
                      <Ionicons name="cart" size={20} color={colors.text.onPrimary} />
                    </TouchableOpacity>
                  )
                )}
              </View>
            </View>
          </View>

          {/* Availability - Enhanced */}
          <View style={styles.availabilitySection}>
            <View style={[styles.availabilityBadge, { backgroundColor: `${String(availabilityColor)}20` }]}>
              <View style={[styles.availabilityDot, { backgroundColor: availabilityColor }]} />
              <Text style={[styles.availabilityText, { color: availabilityColor }]}>
                {availabilityText}
              </Text>
            </View>
            {((selectedVariant && selectedVariant.inventoryQuantity !== undefined) || 
              (!selectedVariant && product.inventoryTracking && product.inventoryQuantity !== undefined)) && (
              <View style={styles.inventoryContainer}>
                <Ionicons name="cube-outline" size={16} color={colors.text.secondary} />
                <Text style={styles.inventoryText}>
                  {selectedVariant && selectedVariant.inventoryQuantity !== undefined
                    ? t('storefront.unitsAvailable', { count: selectedVariant.inventoryQuantity })
                    : t('storefront.unitsAvailable', { count: product.inventoryQuantity })}
                </Text>
              </View>
            )}
          </View>

          {/* Ordering minimums — product quantity + vendor order value */}
          {(minQty > 1 || (vendorMinimums?.vendors?.length ?? 0) > 0) && (
            <View style={styles.minimumsBox}>
              {minQty > 1 && (
                <View style={[styles.minimumsRow, { flexDirection: rtl.rowDirection }]}>
                  <Ionicons name="layers-outline" size={16} color={colors.primary} />
                  <Text style={[styles.minimumsText, { textAlign: rtl.textStart }]}>{t('storefront.minQtyLong', { count: minQty })}</Text>
                </View>
              )}
              {(vendorMinimums?.vendors || []).map((v) => (
                <View key={v.vendorId} style={[styles.minimumsRow, { flexDirection: rtl.rowDirection }]}>
                  <Ionicons name="storefront-outline" size={16} color={colors.primary} />
                  <Text style={[styles.minimumsText, { textAlign: rtl.textStart }]}>
                    {t('storefront.vendorMinimum', { name: v.name, amount: `${v.currency} ${Number(v.minimumOrderAmount).toFixed(2)}` })}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {/* Description - HTML rendered */}
          {product.description && (
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Ionicons name="document-text-outline" size={20} color={colors.primary} />
                <Text style={styles.sectionTitle}>{t('storefront.description')}</Text>
              </View>
              <View style={styles.descriptionContainer}>
                <ProductDescription html={product.description} />
              </View>
            </View>
          )}

          {/* Product Details - Enhanced */}
          {(product.sku || product.barcode || product.weight) && (
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Ionicons name="information-circle-outline" size={20} color={colors.primary} />
                <Text style={styles.sectionTitle}>{t('storefront.details')}</Text>
              </View>
              <View style={styles.detailsGrid}>
                {product.sku && (
                  <View style={styles.detailRow}>
                    <View style={styles.detailIconContainer}>
                      <Ionicons name="barcode-outline" size={20} color={colors.primary} />
                    </View>
                    <View style={styles.detailContent}>
                      <Text style={styles.detailLabel as any}>{t('storefront.sku')}</Text>
                      <Text style={styles.detailValue as any} numberOfLines={2}>{product.sku}</Text>
                    </View>
                  </View>
                )}
                {product.barcode && (
                  <View style={styles.detailRow}>
                    <View style={styles.detailIconContainer}>
                      <Ionicons name="qr-code-outline" size={20} color={colors.primary} />
                    </View>
                    <View style={styles.detailContent}>
                      <Text style={styles.detailLabel as any}>{t('storefront.barcode')}</Text>
                      <Text style={styles.detailValue as any} numberOfLines={1}>{product.barcode}</Text>
                    </View>
                  </View>
                )}
                {!!product.weight && (
                  <View style={styles.detailRow}>
                    <View style={styles.detailIconContainer}>
                      <Ionicons name="scale-outline" size={20} color={colors.primary} />
                    </View>
                    <View style={styles.detailContent}>
                      <Text style={styles.detailLabel as any}>{t('storefront.weight')}</Text>
                      <Text style={styles.detailValue as any}>{product.weight}g</Text>
                    </View>
                  </View>
                )}
              </View>
            </View>
          )}

          {/* ── Addons ── */}
          {product.addons && product.addons.length > 0 && (
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                <Text style={styles.sectionTitle}>{t('storefront.addons')}</Text>
              </View>
              {product.addons.map((addon: any) => {
                const qty = addonSelections[addon.id] || 0;
                const isRequired = addon.isRequired;
                const maxQty = addon.maxQuantity || 10;
                return (
                  <View key={addon.id} style={styles.addonCard}>
                    <View style={styles.addonInfo}>
                      <View style={styles.addonNameRow}>
                        <Text style={styles.addonName}>{addon.name}</Text>
                        {isRequired && (
                          <View style={styles.addonRequiredBadge}>
                            <Text style={styles.addonRequiredText}>{t('storefront.required')}</Text>
                          </View>
                        )}
                      </View>
                      {addon.description ? (
                        <Text style={styles.addonDesc} numberOfLines={2}>{addon.description}</Text>
                      ) : null}
                      <Text style={styles.addonPrice}>
                        +{getCurrencySymbol(product.currency || 'USD')} {addon.price.toFixed(2)}
                      </Text>
                    </View>
                    <View style={styles.addonControls}>
                      {qty > 0 ? (
                        <>
                          <TouchableOpacity
                            style={styles.addonQtyBtn}
                            onPress={() => setAddonSelections(prev => ({
                              ...prev,
                              [addon.id]: Math.max(0, (prev[addon.id] || 0) - 1),
                            }))}
                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                          >
                            <Ionicons name="remove" size={18} color={colors.primary} />
                          </TouchableOpacity>
                          <Text style={styles.addonQtyText}>{qty}</Text>
                          <TouchableOpacity
                            style={styles.addonQtyBtn}
                            onPress={() => setAddonSelections(prev => ({
                              ...prev,
                              [addon.id]: Math.min(maxQty, (prev[addon.id] || 0) + 1),
                            }))}
                            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                          >
                            <Ionicons name="add" size={18} color={colors.primary} />
                          </TouchableOpacity>
                        </>
                      ) : (
                        <TouchableOpacity
                          style={styles.addonAddBtn}
                          onPress={() => setAddonSelections(prev => ({ ...prev, [addon.id]: 1 }))}
                        >
                          <Text style={styles.addonAddBtnText}>{t('storefront.add')}</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                );
              })}
            </View>
          )}

          {/* Variants - Enhanced with different layouts based on count */}
          {product.hasVariants && product.variants && product.variants.length > 0 && (
            <View style={styles.section}>
              {product.variants.length === 1 ? (
                // Single variant - compact inline display (not selectable)
                <View style={styles.singleVariantContainer}>
                  <View style={styles.singleVariantHeader}>
                    <Ionicons name="options-outline" size={18} color={colors.primary} />
                    <Text style={styles.singleVariantLabel}>{t('storefront.variant')}:</Text>
                    <Text style={styles.singleVariantName}>{product.variants[0].name}</Text>
                  </View>
                  <View style={styles.singleVariantDetails}>
                    {product.variants[0].price != null && (
                      <Text style={styles.singleVariantPrice}>
                        {getCurrencySymbol(product.currency || 'USD')} {product.variants[0].price.toFixed(2)}
                      </Text>
                    )}
                    {product.variants[0].inventoryQuantity !== undefined && (
                      <View style={styles.singleVariantInventory}>
                        <Ionicons name="cube-outline" size={14} color={colors.textLight} />
                        <Text style={styles.singleVariantInventoryText}>
                          {t('storefront.unitsAvailable', { count: product.variants[0].inventoryQuantity })}
                        </Text>
                      </View>
                    )}
                  </View>
                </View>
              ) : (
                // Multiple variants - selectable list display
                <>
                  <View style={styles.sectionHeader}>
                    <Ionicons name="options-outline" size={20} color={colors.primary} />
                    <Text style={styles.sectionTitle}>
                      {t('storefront.selectVariant', { count: product.variants.length })}
                    </Text>
                  </View>
                  <View style={styles.variantsContainer}>
                    {product.variants.map((variant: any, index: number) => {
                      const isSelected = selectedVariantId === variant.id;
                      const isOutOfStock = variant.inventoryQuantity !== undefined && variant.inventoryQuantity === 0;
                      return (
                        <TouchableOpacity
                          key={variant.id || index}
                          style={[
                            styles.variantCard,
                            isSelected && styles.variantCardSelected,
                            isOutOfStock && styles.variantCardDisabled,
                          ]}
                          onPress={() => !isOutOfStock && handleVariantSelect(variant.id)}
                          disabled={isOutOfStock}
                          activeOpacity={0.7}
                        >
                          <View style={styles.variantHeader}>
                            <View style={styles.variantNameContainer}>
                              <Text style={[
                                styles.variantName,
                                isSelected && styles.variantNameSelected,
                                isOutOfStock && styles.variantNameDisabled,
                              ]}>
                                {variant.name}
                              </Text>
                              {isSelected && (
                                <Ionicons name="checkmark-circle" size={20} color={colors.primary} style={styles.variantCheckIcon} />
                              )}
                            </View>
                            {variant.price != null && (
                              <Text style={[
                                styles.variantPrice,
                                isSelected && styles.variantPriceSelected,
                                isOutOfStock && styles.variantPriceDisabled,
                              ]}>
                                {`${getCurrencySymbol(product.currency || 'USD')} ${variant.price.toFixed(2)}`}
                              </Text>
                            )}
                          </View>
                          {variant.inventoryQuantity !== undefined && (
                            <View style={styles.variantInventoryContainer}>
                              <Ionicons 
                                name="cube-outline" 
                                size={14} 
                                color={isOutOfStock ? colors.error : colors.textLight} 
                              />
                              <Text style={[
                                styles.variantInventory,
                                isOutOfStock && styles.variantInventoryDisabled,
                              ]}>
                                {variant.inventoryQuantity === 0
                                  ? t('storefront.outOfStock')
                                  : t('storefront.unitsAvailable', { count: variant.inventoryQuantity })}
                              </Text>
                            </View>
                          )}
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                </>
              )}
            </View>
          )}
        </View>
      </ScrollView>

      {/* ── Sticky Add-to-Cart bar ── */}
      {showAddToCart && (
        <View style={styles.stickyWrap}>
          {addonError ? (
            <View style={[styles.stickyError, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="alert-circle" size={16} color={colors.error} />
              <Text style={[styles.stickyErrorText, { textAlign: rtl.textStart }]}>{addonError}</Text>
            </View>
          ) : null}
          <View style={[styles.stickyBar, { flexDirection: rtl.rowDirection }]}>
            <View style={styles.stickyPrice}>
              <Text style={[styles.stickyPriceLabel, { textAlign: rtl.textStart }]}>{t('storefront.price')}</Text>
              <Text style={[styles.stickyPriceVal, { textAlign: rtl.textStart }]}>
                {getCurrencySymbol(product.currency || 'USD')} {displayPrice.toFixed(2)}
              </Text>
            </View>
            {onGoToCart && totalItems > 0 ? (
              <TouchableOpacity style={styles.stickyCartBtn} onPress={onGoToCart} accessibilityLabel={t('storefront.viewCart')}>
                <Ionicons name="bag-handle-outline" size={22} color={colors.text.primary} />
                <View style={styles.stickyCartBadge}>
                  <Text style={styles.stickyCartBadgeText}>{totalItems > 99 ? '99+' : totalItems}</Text>
                </View>
              </TouchableOpacity>
            ) : null}
            {isOutOfStockProduct ? (
              <View style={[styles.stickyBtn, styles.stickyBtnDisabled]}>
                <Text style={styles.stickyBtnTextDisabled}>{t('storefront.outOfStock')}</Text>
              </View>
            ) : product.hasVariants || (product.addons?.length ?? 0) > 0 ? (
              <TouchableOpacity style={[styles.stickyBtn, { flexDirection: rtl.rowDirection }]} onPress={handleAddToCart} activeOpacity={0.85}>
                <Ionicons name="bag-add-outline" size={20} color={colors.text.onPrimary} />
                <Text style={styles.stickyBtnText} numberOfLines={1}>
                  {currentQuantity > 0 ? t('storefront.addAgain', { count: currentQuantity }) : t('storefront.addToCart')}
                </Text>
              </TouchableOpacity>
            ) : currentQuantity > 0 ? (
              <View style={[styles.stickyQtyRow, { flexDirection: rtl.rowDirection }]}>
                <TouchableOpacity style={styles.stickyQtyBtn} onPress={handleDecreaseQuantity} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                  <Ionicons name={currentQuantity <= minQty ? 'trash-outline' : 'remove'} size={20} color={colors.primary} />
                </TouchableOpacity>
                <Text style={styles.stickyQtyText}>{currentQuantity}</Text>
                <TouchableOpacity style={styles.stickyQtyBtn} onPress={handleIncreaseQuantity} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                  <Ionicons name="add" size={20} color={colors.primary} />
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity style={[styles.stickyBtn, { flexDirection: rtl.rowDirection }]} onPress={handleAddToCart} activeOpacity={0.85}>
                <Ionicons name="bag-add-outline" size={20} color={colors.text.onPrimary} />
                <Text style={styles.stickyBtnText} numberOfLines={1}>{t('storefront.addToCart')}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}
    </Animated.View>
  );
};

// Import styles from ProductViewScreen - we'll need to copy them
const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  } as any,
  content: {
    flex: 1,
    width: '100%',
  },
  contentContainer: {
    paddingBottom: spacing.xl * 2,
    width: '100%',
    ...Platform.select({
      web: {
        boxSizing: 'border-box',
      },
    }),
  } as any,
  imageSection: {
    backgroundColor: colors.background.surface,
    marginBottom: spacing.md,
    ...designTokens.shadows.sm,
    width: '100%',
    position: 'relative',
  },
  imageGalleryContainer: {
    width: '100%',
    position: 'relative',
  },
  imageGallery: {
    width: '100%',
    height: '100%',
  },
  cartBadge: {
    position: 'absolute',
    top: spacing.md,
    right: spacing.md,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.full,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    zIndex: 15,
    ...designTokens.shadows.md,
    ...Platform.select({
      web: {
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      },
    }),
  },
  cartBadgeText: {
    ...textStyles.caption,
    color: colors.white,
    fontWeight: '700',
    fontSize: 12,
    lineHeight: 14,
  },
  imageCounter: {
    position: 'absolute',
    top: spacing.md,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.full,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    zIndex: 10,
    ...designTokens.shadows.md,
  },
  imageCounterText: {
    ...textStyles.caption,
    color: colors.white,
    fontWeight: '600',
  },
  discountBadge: {
    position: 'absolute',
    top: spacing.md,
    backgroundColor: colors.error,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
    zIndex: 10,
    ...designTokens.shadows.md,
  },
  discountText: {
    ...textStyles.caption,
    color: colors.white,
    fontWeight: '700',
    fontSize: 14,
  },
  imageFooter: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    zIndex: 5,
    ...Platform.select({
      web: {
        background: 'linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.4), transparent)',
      },
    }),
  },
  imageFooterContent: {
    width: '100%',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  imageFooterBrand: {
    ...textStyles.body,
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
    textTransform: 'uppercase' as any,
    letterSpacing: 1.2,
    marginBottom: spacing.xs,
    opacity: 0.95,
    ...Platform.select({
      web: {
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6)',
      },
      default: {
        textShadowColor: 'rgba(0, 0, 0, 0.75)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 3,
      },
    }),
  } as any,
  imageFooterName: {
    ...textStyles.h2,
    color: colors.white,
    fontWeight: '700',
    fontSize: 22,
    lineHeight: 28,
    ...Platform.select({
      web: {
        textShadow: '0 2px 4px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6)',
      },
      default: {
        textShadowColor: 'rgba(0, 0, 0, 0.75)',
        textShadowOffset: { width: 0, height: 1 },
        textShadowRadius: 3,
      },
    }),
  } as any,
  thumbnailsContainer: {
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    backgroundColor: colors.background.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  thumbnailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  thumbnailsScrollView: {
    flex: 1,
  },
  thumbnailsScrollContent: {
    alignItems: 'center',
  },
  thumbnail: {
    width: 60,
    height: 60,
    borderRadius: designTokens.borderRadius.md,
    marginRight: spacing.sm,
    borderWidth: 2,
    borderColor: 'transparent',
    overflow: 'hidden',
  },
  thumbnailActive: {
    borderColor: colors.primary,
    ...designTokens.shadows.sm,
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
  },
  imagePlaceholder: {
    backgroundColor: colors.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  placeholderText: {
    ...textStyles.body,
    color: colors.textLight,
    marginTop: spacing.sm,
  } as any,
  productCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.lg,
    marginHorizontal: spacing.md,
    marginBottom: spacing.md,
    ...designTokens.shadows.md,
    alignSelf: 'stretch',
    ...Platform.select({
      web: {
        boxSizing: 'border-box',
        width: 'calc(100% - ' + (spacing.md * 2) + 'px)',
      },
      default: {
        width: '100%',
      },
    }),
  } as any,
  priceSection: {
    marginBottom: spacing.lg,
    paddingBottom: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.md,
    marginBottom: spacing.sm,
    flexWrap: 'wrap',
  },
  price: {
    ...textStyles.h2,
    color: colors.primary,
    fontWeight: '700',
    fontSize: 24,
    lineHeight: 30,
  } as any,
  originalPrice: {
    ...textStyles.body,
    color: colors.textLight,
    textDecorationLine: 'line-through',
    marginLeft: spacing.sm,
    fontSize: 16,
    lineHeight: 20,
  } as any,
  priceContainer: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing.sm,
    flexWrap: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  priceActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
  },
  discountTagContainer: {
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: spacing.xs,
    flexShrink: 0,
  },
  originalPriceBelowBadge: {
    ...textStyles.caption,
    color: colors.textLight,
    textDecorationLine: 'line-through',
    fontSize: 12,
    lineHeight: 16,
  } as any,
  discountTag: {
    backgroundColor: `${String(colors.success)}20`,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.md,
    flexShrink: 0,
  },
  discountTagText: {
    ...textStyles.caption,
    color: colors.success,
    fontWeight: '600',
  } as any,
  availabilitySection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.lg,
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  availabilityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: designTokens.borderRadius.full,
    gap: spacing.xs,
  },
  availabilityDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  availabilityText: {
    fontSize: 14,
    fontFamily: typography.fontFamily.semibold,
    fontWeight: '600' as any,
    color: colors.text.primary,
  } as any,
  inventoryText: {
    ...textStyles.body,
    color: colors.text.secondary,
    fontSize: 15,
    fontWeight: '500',
    marginTop: spacing.xs,
  } as any,
  section: {
    marginBottom: spacing.xl,
    width: '100%',
    ...Platform.select({
      web: {
        boxSizing: 'border-box',
      },
    }),
  } as any,
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  sectionTitle: {
    ...textStyles.h3,
    color: colors.text.primary as any,
    marginBottom: 0,
    fontWeight: '600',
    flex: 1,
  } as any,
  description: {
    ...textStyles.body,
    color: colors.text.secondary as any,
    lineHeight: 24,
    fontSize: 16,
  } as any,
  descriptionContainer: {
    backgroundColor: colors.lightGray,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    marginTop: spacing.sm,
  },
  detailsGrid: {
    gap: spacing.sm,
    width: '100%',
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    width: '100%',
    minWidth: 0,
  },
  detailIconContainer: {
    width: 40,
    height: 40,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: `${String(colors.primary)}15`,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
    flexShrink: 0,
  },
  detailContent: {
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  },
  detailLabel: {
    ...textStyles.caption,
    color: colors.textLight,
    marginBottom: spacing.xs,
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: 0.5,
  },
  detailValue: {
    ...textStyles.body,
    color: colors.text.primary,
    fontWeight: '500',
    fontSize: 15,
    lineHeight: 20,
    flexWrap: 'wrap',
  } as any,
  variantCard: {
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 2,
    borderColor: colors.border,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  variantCardSelected: {
    backgroundColor: `${String(colors.primary)}10`,
    borderColor: colors.primary,
    borderWidth: 2,
    ...designTokens.shadows.sm,
  },
  variantCardDisabled: {
    opacity: 0.6,
    backgroundColor: colors.lightGray,
  },
  variantHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  variantsContainer: {
    gap: spacing.sm,
  },
  variantNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
    gap: spacing.xs,
  },
  variantName: {
    ...textStyles.body,
    color: colors.text.primary as any,
    fontWeight: '600',
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
  } as any,
  variantNameSelected: {
    color: colors.primary as any,
    fontWeight: '700',
  } as any,
  variantNameDisabled: {
    color: colors.textLight as any,
    textDecorationLine: 'line-through',
  } as any,
  variantCheckIcon: {
    marginLeft: spacing.xs,
  },
  variantPrice: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    fontWeight: '600' as any,
    color: colors.primary,
    lineHeight: typography.lineHeight.relaxed,
    flexShrink: 0,
    marginLeft: spacing.sm,
  } as any,
  variantPriceSelected: {
    color: colors.primary,
    fontWeight: '700' as any,
    fontSize: typography.fontSize.lg,
  } as any,
  variantPriceDisabled: {
    color: colors.textLight,
  } as any,
  variantInventory: {
    ...textStyles.caption,
    color: colors.textLight,
  } as any,
  variantInventoryDisabled: {
    color: colors.error,
    fontWeight: '600' as any,
  } as any,
  singleVariantContainer: {
    backgroundColor: colors.lightGray,
    padding: spacing.md,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  singleVariantHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.sm,
  },
  singleVariantLabel: {
    ...textStyles.caption,
    color: colors.textLight,
    fontWeight: '600',
  } as any,
  singleVariantName: {
    ...textStyles.body,
    color: colors.text.primary,
    fontWeight: '600',
    flex: 1,
  } as any,
  singleVariantDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  singleVariantPrice: {
    ...textStyles.body,
    color: colors.primary,
    fontWeight: '700',
    fontSize: 16,
  } as any,
  singleVariantInventory: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  singleVariantInventoryText: {
    ...textStyles.caption,
    color: colors.textLight,
    fontSize: 12,
  } as any,
  variantInventoryContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  addToCartButtonThumbnail: {
    width: 60,
    height: 60,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.md,
    flexShrink: 0,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  } as any,
  addToCartButtonInline: {
    width: 48,
    height: 48,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.md,
    flexShrink: 0,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  } as any,
  inventoryContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  quantityControlsThumbnail: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs / 2,
    flexShrink: 0,
    minWidth: 100,
    justifyContent: 'center',
  },
  quantityControlsInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs / 2,
    flexShrink: 0,
    minWidth: 80,
    justifyContent: 'center',
  },
  quantityButton: {
    padding: spacing.xs / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quantityTextThumbnail: {
    ...textStyles.body,
    color: colors.text.primary,
    fontSize: 16,
    fontWeight: '700',
    minWidth: 24,
    textAlign: 'center',
  } as any,
  quantityTextInline: {
    ...textStyles.body,
    color: colors.text.primary,
    fontSize: 14,
    fontWeight: '700',
    minWidth: 20,
    textAlign: 'center',
  } as any,

  // Addon styles
  addonCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.lightGray,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.border,
    gap: spacing.md,
  },
  addonInfo: {
    flex: 1,
    minWidth: 0,
  },
  addonNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
    marginBottom: 2,
  },
  addonName: {
    ...textStyles.body,
    color: colors.text.primary,
    fontWeight: '600',
    fontSize: 15,
  } as any,
  addonRequiredBadge: {
    backgroundColor: hexAlpha(String(colors.error), 0.15),
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  addonRequiredText: {
    color: colors.error,
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase' as any,
    letterSpacing: 0.4,
  } as any,
  addonDesc: {
    ...textStyles.caption,
    color: colors.textLight,
    fontSize: 12,
    marginBottom: 4,
  } as any,
  addonPrice: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: '700',
    fontFamily: (textStyles.body as any).fontFamily,
  } as any,
  addonControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexShrink: 0,
  },
  addonQtyBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1.5,
    borderColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addonQtyText: {
    color: colors.text.primary,
    fontSize: 15,
    fontWeight: '700',
    minWidth: 20,
    textAlign: 'center',
    fontFamily: (textStyles.body as any).fontFamily,
  } as any,
  addonAddBtn: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addonAddBtnText: {
    color: colors.text.onPrimary,
    fontSize: 13,
    fontWeight: '700',
    fontFamily: (textStyles.body as any).fontFamily,
  } as any,

  minimumsBox: {
    marginTop: spacing.sm,
    padding: spacing.sm,
    borderRadius: 12,
    gap: 6,
    backgroundColor: colors.primaryTransparent,
  },
  minimumsRow: { alignItems: 'center', gap: 8 },
  minimumsText: { flex: 1, fontSize: 13, lineHeight: 18, color: colors.text.primary, fontFamily: typography.fontFamily.medium } as any,

  // ── Sticky bottom cart bar ────────────────────────────────────────────────
  stickyWrap: {
    position: 'absolute' as any,
    bottom: 0, left: 0, right: 0,
    backgroundColor: colors.background.surface,
    borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.border,
    ...Platform.select({
      ios: { shadowColor: '#000', shadowOffset: { width: 0, height: -2 }, shadowOpacity: 0.08, shadowRadius: 8 },
      android: { elevation: 8 },
      web: { boxShadow: '0 -2px 12px rgba(0,0,0,0.12)' } as any,
    }),
  } as any,
  stickyError: { alignItems: 'center', gap: 6, paddingHorizontal: 16, paddingTop: 10 },
  stickyErrorText: { flex: 1, fontSize: 13, color: colors.error, fontFamily: typography.fontFamily.medium } as any,
  stickyBar: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 16, paddingVertical: 12, gap: 10,
  } as any,
  stickyPrice: { flex: 1, minWidth: 0 },
  stickyPriceLabel: { fontSize: 11, color: colors.text.secondary, fontFamily: typography.fontFamily.medium } as any,
  stickyPriceVal: { fontSize: 19, color: colors.text.primary, fontFamily: typography.fontFamily.bold } as any,
  stickyCartBtn: {
    width: 48, height: 48, borderRadius: 14,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: colors.background.secondary,
    borderWidth: 1, borderColor: colors.border,
  },
  stickyCartBadge: {
    position: 'absolute', top: -4, right: -4,
    minWidth: 18, height: 18, borderRadius: 9, paddingHorizontal: 4,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  stickyCartBadgeText: { fontSize: 10, lineHeight: 12, color: colors.text.onPrimary, fontFamily: typography.fontFamily.bold } as any,
  stickyBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: colors.primary, borderRadius: 14,
    height: 48, paddingHorizontal: 18, gap: 8, minWidth: 140, maxWidth: 240,
  },
  stickyBtnDisabled: { backgroundColor: colors.background.disabled },
  stickyBtnText: { color: colors.text.onPrimary, fontSize: 15, fontFamily: typography.fontFamily.bold, flexShrink: 1 } as any,
  stickyBtnTextDisabled: { color: colors.text.disabled, fontSize: 14, fontFamily: typography.fontFamily.semibold } as any,
  stickyQtyRow: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: colors.primaryTransparent, borderRadius: 14, overflow: 'hidden',
    borderWidth: 1.5, borderColor: colors.primary, height: 48,
  },
  stickyQtyBtn: { width: 44, height: 46, justifyContent: 'center', alignItems: 'center' },
  stickyQtyText: { fontSize: 17, color: colors.text.primary, minWidth: 36, textAlign: 'center', fontFamily: typography.fontFamily.bold } as any,
});

